import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getContactInfo, updateContactInfo, getContactMessages, markMessageAsRead } from '../../services/api';
import '../../styles/Admin.css';

const EditContact = () => {
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    address: '',
    linkedin_url: '',
    instagram_url: '',
    behance_url: ''
  });
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      // جلب معلومات التواصل
      const contactResponse = await getContactInfo();
      const contactData = contactResponse.data || contactResponse;
      if (contactData) {
        setFormData({
          email: contactData.email || '',
          phone: contactData.phone || '',
          address: contactData.address || '',
          linkedin_url: contactData.linkedin_url || '',
          instagram_url: contactData.instagram_url || '',
          behance_url: contactData.behance_url || ''
        });
      }

      // جلب الرسائل
      const messagesResponse = await getContactMessages();
      const messagesData = Array.isArray(messagesResponse)
        ? messagesResponse
        : (messagesResponse.data || []);
      setMessages(messagesData);

    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'فشل في تحميل البيانات' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // التحقق
    if (formData.email && !validateEmail(formData.email)) {
      setMessage({ type: 'error', text: 'يرجى إدخال عنوان بريد إلكتروني صحيح' });
      return;
    }

    if (formData.linkedin_url && !validateURL(formData.linkedin_url)) {
      setMessage({ type: 'error', text: 'يرجى إدخال رابط LinkedIn صحيح' });
      return;
    }

    if (formData.instagram_url && !validateURL(formData.instagram_url)) {
      setMessage({ type: 'error', text: 'يرجى إدخال رابط Instagram صحيح' });
      return;
    }

    if (formData.behance_url && !validateURL(formData.behance_url)) {
      setMessage({ type: 'error', text: 'يرجى إدخال رابط Behance صحيح' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await updateContactInfo(formData);
      setMessage({ type: 'success', text: 'تم تحديث معلومات التواصل بنجاح!' });
    } catch (error) {
      console.error('Error updating contact info:', error);
      setMessage({ type: 'error', text: 'فشل في تحديث معلومات التواصل' });
    } finally {
      setSaving(false);
    }
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validateURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleViewMessages = () => {
    setShowMessagesModal(true);
  };

  const handleOpenMessage = async (msg) => {
    setSelectedMessage(msg);
    
    // تعليم الرسالة كمقروءة
    if (msg.status === 'new') {
      try {
        await markMessageAsRead(msg.id);
        await fetchData(); // تحديث القائمة
      } catch (error) {
        console.error('Error marking message as read:', error);
      }
    }
  };

  const handleCloseMessage = () => {
    setSelectedMessage(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  const unreadCount = messages.filter(m => m.status === 'new').length;

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div className="admin-layout" dir="rtl">
      {/* Mobile Menu Button */}
      <button 
        className={`mobile-menu-btn ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Sidebar Overlay */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <h2 className="admin-sidebar-logo">لوحة التحكم</h2>
          <p className="admin-sidebar-subtitle">إدارة المعرض</p>
        </div>

        <nav>
          <ul className="admin-nav">
            <li className="admin-nav-item">
              <Link to="/admin/dashboard" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">📊</span>
                <span>الرئيسية</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/hero" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🎯</span>
                <span>قسم الرئيسي</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/about" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">👤</span>
                <span>نبذة عني</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/projects" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🏗️</span>
                <span>المشاريع</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/contact" className="admin-nav-link active" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">📧</span>
                <span>معلومات التواصل</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/change-password" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🔒</span>
                <span>تغيير كلمة المرور</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <a href="/" className="admin-nav-link" target="_blank" rel="noopener noreferrer">
                <span className="nav-icon">🌐</span>
                <span>عرض الموقع</span>
              </a>
            </li>
          </ul>
        </nav>

        <div className="admin-sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <span>🚪</span>
            تسجيل الخروج
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-header">
          <div>
            <h1>معلومات التواصل</h1>
            <p>تحديث بيانات الاتصال وعرض الرسائل</p>
          </div>
          <button className="btn-view-messages" onClick={handleViewMessages}>
            <span>💬</span>
            عرض الرسائل
            {unreadCount > 0 && (
              <span className="messages-badge">{unreadCount}</span>
            )}
          </button>
        </div>

        <div className="admin-content">
          {message.text && (
            <div className={`alert alert-${message.type}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="admin-form">
            {/* Basic Contact Info */}
            <div className="form-section">
              <h3 className="form-section-title">📧 المعلومات الأساسية</h3>

              <div className="form-group">
                <label htmlFor="email">
                  البريد الإلكتروني
                  <span className="field-hint">بريدك الإلكتروني المهني</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="architect@example.com"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">
                  رقم الهاتف
                  <span className="field-hint">رقم هاتف التواصل</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+961 XX XXX XXX"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">
                  العنوان
                  <span className="field-hint">موقع المكتب أو المدينة</span>
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="بيروت، لبنان"
                  rows="3"
                  disabled={saving}
                />
              </div>
            </div>

            {/* Social Media */}
            <div className="form-section">
              <h3 className="form-section-title">🔗 روابط التواصل الاجتماعي</h3>

              <div className="form-group">
                <label htmlFor="linkedin_url">
                  رابط LinkedIn
                  <span className="field-hint">ملفك الشخصي على LinkedIn</span>
                </label>
                <input
                  type="url"
                  id="linkedin_url"
                  name="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/in/yourname"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="instagram_url">
                  رابط Instagram
                  <span className="field-hint">ملفك الشخصي على Instagram</span>
                </label>
                <input
                  type="url"
                  id="instagram_url"
                  name="instagram_url"
                  value={formData.instagram_url}
                  onChange={handleChange}
                  placeholder="https://instagram.com/yourname"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="behance_url">
                  رابط Behance
                  <span className="field-hint">معرض أعمالك على Behance</span>
                </label>
                <input
                  type="url"
                  id="behance_url"
                  name="behance_url"
                  value={formData.behance_url}
                  onChange={handleChange}
                  placeholder="https://behance.net/yourname"
                  disabled={saving}
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="form-actions">
              <button
                type="submit"
                className="btn-save"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-small"></span>
                    جاري الحفظ...
                  </>
                ) : (
                  <>
                    <span>💾</span>
                    حفظ التغييرات
                  </>
                )}
              </button>

              <Link to="/admin/dashboard" className="btn-cancel">
                إلغاء
              </Link>
            </div>
          </form>
        </div>
      </main>

      {/* Messages Modal */}
      {showMessagesModal && (
        <div className="modal-overlay" onClick={() => setShowMessagesModal(false)}>
          <div className="modal-container messages-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-admin">
              <h2>
                رسائل التواصل
                {unreadCount > 0 && (
                  <span className="messages-badge-large">{unreadCount} جديدة</span>
                )}
              </h2>
              <button className="modal-close-btn" onClick={() => setShowMessagesModal(false)}>✕</button>
            </div>

            <div className="modal-body-admin">
              {messages.length === 0 ? (
                <div className="empty-state">
                  <span className="empty-icon">📭</span>
                  <p>لا توجد رسائل بعد</p>
                </div>
              ) : (
                <div className="messages-list-admin">
                  {messages.map((msg) => (
                    <div 
                      key={msg.id} 
                      className={`message-item ${msg.status === 'new' ? 'unread' : ''}`}
                      onClick={() => handleOpenMessage(msg)}
                    >
                      <div className="message-item-header">
                        <div className="message-from">
                          <strong>{msg.name}</strong>
                          {msg.status === 'new' && (
                            <span className="new-badge">جديدة</span>
                          )}
                        </div>
                        <span className="message-date">
                          {new Date(msg.created_at).toLocaleDateString('ar-SA', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="message-email">{msg.email}</div>
                      <div className="message-preview">
                        {msg.message.substring(0, 100)}
                        {msg.message.length > 100 && '...'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Single Message Modal */}
      {selectedMessage && (
        <div className="modal-overlay" onClick={handleCloseMessage}>
          <div className="modal-container single-message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-admin">
              <h2>تفاصيل الرسالة</h2>
              <button className="modal-close-btn" onClick={handleCloseMessage}>✕</button>
            </div>

            <div className="modal-body-admin">
              <div className="single-message-content">
                <div className="message-detail-item">
                  <strong>من:</strong>
                  <span>{selectedMessage.name}</span>
                </div>
                <div className="message-detail-item">
                  <strong>البريد الإلكتروني:</strong>
                  <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a>
                </div>
                <div className="message-detail-item">
                  <strong>التاريخ:</strong>
                  <span>
                    {new Date(selectedMessage.created_at).toLocaleString('ar-SA', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="message-detail-item message-body">
                  <strong>الرسالة:</strong>
                  <p>{selectedMessage.message}</p>
                </div>
                <div className="message-actions">
                  <a 
                    href={`mailto:${selectedMessage.email}?subject=Re: رسالتك`}
                    className="btn-reply"
                  >
                    📧 الرد عبر البريد الإلكتروني
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditContact;